import { useCallback, useEffect, useRef, useState } from "react";
import { FileDropTarget } from "@/components/FileDropTarget";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  presignDocument,
  confirmDocument,
  processBatch,
  screenFile,
  uploadToPresigned,
  type FileRejectionReason,
  type IntakeProposal,
  type IntakeTarget,
  type Uploader,
} from "@/lib/brokerDocuments";
import { messageFor } from "@/lib/errors";

// BRK-S038 — the real New Business dropzone. A broker drops one or more PDFs
// (any mix of ACORD 125/126/130 + prior policies); this uploads each straight to
// storage via its presigned PUT (bytes bypass the app tier), confirms it, then
// processes the batch and polls until the proposal is ready. Honest states
// throughout: per-file progress, an explicit processing state, and a report-back
// of everything extraction could NOT use (unreadable pages, failed documents,
// unmatched class codes) — never silently dropped (AC #2). On success it hands
// the proposal UP; the SAME manual forms downstream consume it (single capture
// surface). "Start without a document" stays the manual path (rendered by the
// parent) — this component is purely the document path.

type FileStatus = "queued" | "uploading" | "uploaded" | "error";

interface TrackedFile {
  id: string;
  file: File;
  status: FileStatus;
  /** 0–100 upload progress. */
  progress: number;
  error?: string;
}

interface Rejection {
  name: string;
  reason: FileRejectionReason;
}

type Phase = "select" | "working" | "done" | "failed";

const REJECTION_COPY: Record<FileRejectionReason, string> = {
  not_pdf: "isn’t a PDF — only PDF documents can be read.",
  too_large: "is over the 32 MB limit.",
};

/** The watchdog's own rejection, distinguishable from a server failure. */
const TIMED_OUT = Symbol("process timed out");

function isTimeout(err: unknown): boolean {
  return err === TIMED_OUT;
}

/** `work`, or a rejection once `ms` has passed. The pending work is left to
 *  settle on its own — there is nothing to cancel server-side, and the caller
 *  has already moved the broker on. */
function withTimeout<T>(work: Promise<T>, ms: number | undefined): Promise<T> {
  if (ms === undefined) return work;
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(TIMED_OUT), ms);
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
}

export function DocumentDropzone({
  authedRequest,
  onProposal,
  uploader = uploadToPresigned,
  target = "submission",
  prompt = "Drag & drop your ACORD forms or prior policies, or",
  hint = "PDF only, up to 32 MB each. We’ll read them and fill in what we can.",
  inputLabel = "Upload documents",
  actionLabel = "Analyze documents",
  busyLabel = "Reading your documents…",
  failureMessage = "We couldn’t read those documents. You can try again, or start without a document and enter the details yourself.",
  multiple = true,
  processTimeoutMs,
  summaryTitle = "Documents analyzed",
  summaryLine,
}: {
  authedRequest: AuthedRequester;
  /** Handed the proposal once the batch is processed — the parent prefills the
   *  flow from it. Called exactly once per successful analysis. */
  onProposal: (proposal: IntakeProposal) => void;
  /** Injected for the dev-login offline path (no direct-to-storage network) and
   *  for tests; defaults to the real XHR uploader. */
  uploader?: Uploader;
  /**
   * BRK-S120 — which intake the batch is opened for. A bond request is its own
   * target (PMS-S269): reusing `submission` would route it through the P&C merge
   * buckets. The COPY below is parameterised for the same reason the target is —
   * one real upload component, so the presign contract has exactly one caller
   * shape to drift from (the BRK-S038 guard's whole point).
   */
  target?: IntakeTarget;
  prompt?: string;
  hint?: string;
  /** The file input's accessible name — what a test and a screen reader find. */
  inputLabel?: string;
  actionLabel?: string;
  busyLabel?: string;
  /** What to say when processing fails or the batch comes back `failed`. */
  failureMessage?: string;
  /** False for an intake that takes exactly one document. */
  multiple?: boolean;
  /**
   * A watchdog on `/process` (BRK-S120). Extraction latency is a live-demo risk,
   * and a request that never answers would leave the broker watching a spinner
   * with a perfectly usable form underneath it. On expiry this degrades to the
   * SAME handled failure state a rejection produces — the proposal is never
   * handed up late, because by then the broker is typing. Omitted means wait.
   */
  processTimeoutMs?: number;
  summaryTitle?: string;
  /**
   * What the analysed summary SAYS it did, when the caller knows better than
   * this component does. The default sentence ("we've filled in what we could")
   * is true for an ACORD packet, where the proposal IS the prefill; a caller
   * that maps the proposal onto its own fields knows how many landed — and a
   * document that yielded nothing must not be summarised as a success (BRK-S120).
   */
  summaryLine?: string;
}) {
  const [files, setFiles] = useState<TrackedFile[]>([]);
  const [rejected, setRejected] = useState<Rejection[]>([]);
  const [phase, setPhase] = useState<Phase>("select");
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<IntakeProposal | null>(null);
  const seq = useRef(0);
  // Guard against setState after unmount (the poll loop is async).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const accepted: TrackedFile[] = [];
    const bad: Rejection[] = [];
    for (const file of Array.from(incoming)) {
      const reason = screenFile(file);
      if (reason) bad.push({ name: file.name, reason });
      else
        accepted.push({
          id: `f${(seq.current += 1)}`,
          file,
          status: "queued",
          progress: 0,
        });
    }
    if (accepted.length) setFiles((f) => [...f, ...accepted]);
    setRejected(bad);
  }, []);

  function removeFile(id: string) {
    setFiles((f) => f.filter((x) => x.id !== id));
  }

  function patchFile(id: string, patch: Partial<TrackedFile>) {
    if (!mounted.current) return;
    setFiles((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async function analyze() {
    if (!files.length) return;
    setPhase("working");
    setError(null);

    // 1) Presign each file into ONE batch (sequential so the batch id carries),
    //    then upload all in parallel with per-file progress, then confirm.
    let batchId: string | undefined;
    const tickets: {
      tracked: TrackedFile;
      documentId: string;
      upload: NonNullable<
        Awaited<ReturnType<typeof presignDocument>>
      >["upload"];
    }[] = [];
    try {
      for (const tracked of files) {
        // File 1 sends `target` (opens the batch); files 2..N send the captured
        // `batchId` (join it) — exactly one, per the contract. Sequential, so the
        // batch id from file 1 is in hand before file 2 presigns.
        const res = await presignDocument(
          authedRequest,
          batchId === undefined
            ? {
                filename: tracked.file.name,
                contentType: "application/pdf",
                target,
              }
            : {
                filename: tracked.file.name,
                contentType: "application/pdf",
                batchId,
              },
        );
        batchId = res.batchId;
        tickets.push({
          tracked,
          documentId: res.documentId,
          upload: res.upload,
        });
      }
    } catch (err) {
      if (mounted.current) {
        setError(messageFor(err));
        setPhase("select");
      }
      return;
    }

    const uploads = tickets.map(async ({ tracked, documentId, upload }) => {
      // The presigned PUT is its OWN step. A non-2xx or thrown upload means the
      // bytes never landed in storage, so we must NOT confirm that file —
      // confirming a missing object surfaces the failure in the wrong place
      // ("object not found in storage"). It's an immediate per-file failure with
      // its own message; confirm runs ONLY after a successful upload.
      patchFile(tracked.id, { status: "uploading", progress: 0 });
      try {
        await uploader(upload, tracked.file, {
          onProgress: ({ loaded, total }) =>
            patchFile(tracked.id, {
              progress: total ? Math.round((loaded / total) * 100) : 0,
            }),
        });
      } catch {
        patchFile(tracked.id, {
          status: "error",
          error:
            "Couldn’t upload this file — check your connection and try again.",
        });
        throw new Error("upload failed");
      }
      try {
        await confirmDocument(authedRequest, documentId);
        patchFile(tracked.id, { status: "uploaded", progress: 100 });
      } catch (err) {
        patchFile(tracked.id, { status: "error", error: messageFor(err) });
        throw err;
      }
    });

    const results = await Promise.allSettled(uploads);
    if (!mounted.current) return;
    if (results.some((r) => r.status === "rejected") || !batchId) {
      setError(
        "Some documents didn’t upload. Remove the failed ones and try again, or start without a document.",
      );
      setPhase("select");
      return;
    }

    // 2) Fold the batch into the proposal (PMS-S131 `/process` returns it
    //    synchronously with a settled status — no polling needed).
    try {
      const proposal = await withTimeout(
        processBatch(authedRequest, batchId),
        processTimeoutMs,
      );
      if (!mounted.current) return;
      if (proposal.status === "failed") {
        setPhase("failed");
        return;
      }
      setProposal(proposal);
      setPhase("done");
      onProposal(proposal);
    } catch (err) {
      if (mounted.current) {
        // A watchdog expiry has nothing to report but the failure itself: the
        // request may still be in flight, so `messageFor` would name a timeout
        // the broker can do nothing about. The failure copy is the whole answer.
        setError(isTimeout(err) ? null : messageFor(err));
        setPhase("failed");
      }
    }
  }

  function reset() {
    setFiles([]);
    setRejected([]);
    setProposal(null);
    setError(null);
    setPhase("select");
  }

  // ── Rendering ───────────────────────────────────────────────────────────────
  if (phase === "done" && proposal) {
    return (
      <AnalyzedSummary
        proposal={proposal}
        onReset={reset}
        title={summaryTitle}
        line={summaryLine}
      />
    );
  }

  const busy = phase === "working";

  return (
    <div className="flex flex-col gap-3">
      {/* The drop affordance is shared with the bond order's `files` field
          (BRK-S145) — same dashed region, same Browse-over-a-hidden-input, and
          none of this component's upload/analyse state. */}
      <FileDropTarget
        prompt={prompt}
        hint={hint}
        inputLabel={inputLabel}
        accept="application/pdf,.pdf"
        multiple={multiple}
        disabled={busy}
        onFiles={addFiles}
      />

      {rejected.length > 0 && (
        <ul
          role="alert"
          className="flex flex-col gap-1 text-sm text-brk-danger"
        >
          {rejected.map((r) => (
            <li key={r.name}>
              <span className="font-medium">{r.name}</span>{" "}
              {REJECTION_COPY[r.reason]}
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex flex-col gap-1 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-brk-text">
                  {f.file.name}
                </span>
                {f.status === "queued" && !busy ? (
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    className="shrink-0 text-brk-danger hover:underline"
                  >
                    Remove
                  </button>
                ) : (
                  <span className="shrink-0 text-xs text-brk-dim">
                    {f.status === "uploading"
                      ? `Uploading ${f.progress}%`
                      : f.status === "uploaded"
                        ? "Uploaded"
                        : f.status === "error"
                          ? "Failed"
                          : "Ready"}
                  </span>
                )}
              </div>
              {(f.status === "uploading" || f.status === "uploaded") && (
                <div
                  className="h-1 w-full overflow-hidden rounded-full bg-brk-border"
                  role="progressbar"
                  aria-label={`Uploading ${f.file.name}`}
                  aria-valuenow={f.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full bg-brk-accent transition-all"
                    style={{ width: `${f.progress}%` }}
                  />
                </div>
              )}
              {f.error && (
                <span className="text-xs text-brk-danger">{f.error}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* The upload-side failures (presign, the PUT, confirm) — each already
          per-file above; this is the step-level summary of one. */}
      {error && phase !== "failed" && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {/* A failed READ is ONE sentence, whatever went wrong: a `failed` batch, a
          rejection, or the watchdog. The server's own words ride BELOW it rather
          than instead of it — "Extraction failed." tells a broker nothing they
          can act on, and it used to be all they got (BRK-S120). */}
      {phase === "failed" && (
        <div role="alert" className="flex flex-col gap-1">
          <p className="text-sm text-brk-danger">{failureMessage}</p>
          {error && <p className="text-xs text-brk-dim">{error}</p>}
        </div>
      )}

      {files.length > 0 && (
        <button
          type="button"
          onClick={() => void analyze()}
          disabled={busy || files.every((f) => f.status === "error")}
          className="self-start rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
        >
          {busy ? busyLabel : actionLabel}
        </button>
      )}
    </div>
  );
}

// The processing result: what we read, and — honestly — what we couldn't. The
// broker continues into the prefilled flow (already handed the proposal); this
// stays visible so unreadable pages / failed docs are never silently dropped.
function AnalyzedSummary({
  proposal,
  onReset,
  title,
  line,
}: {
  proposal: IntakeProposal;
  onReset: () => void;
  title: string;
  line?: string;
}) {
  const { report, coverageFormsFound } = proposal;
  const hasGaps =
    report.unrecognized.length > 0 ||
    report.failures.length > 0 ||
    report.unmapped.length > 0;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-brk-text">
            <span aria-hidden="true">✓ </span>
            {title}
          </span>
          <span className="text-sm text-brk-dim">
            {line !== undefined
              ? line
              : coverageFormsFound.length > 0
                ? `We recognized ${formList(coverageFormsFound)}. We’ve filled in what we could below — review each step.`
                : "We’ve filled in what we could below — review each step."}
          </span>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="shrink-0 text-sm text-brk-accent hover:underline"
        >
          Start over
        </button>
      </div>

      {hasGaps && (
        <div className="flex flex-col gap-2 rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm">
          <span className="font-medium text-brk-text">
            A few things you’ll need to enter yourself:
          </span>
          <ul className="flex list-inside list-disc flex-col gap-1 text-brk-dim">
            {report.failures.map((f) => (
              <li key={f.documentId}>
                <span className="font-medium">{f.filename}</span> couldn’t be
                read ({f.error}) — enter its details manually.
              </li>
            ))}
            {report.unrecognized.map((u) => (
              <li key={`${u.documentId}-${u.pageStart}`}>
                <span className="font-medium">{u.filename}</span>{" "}
                {u.pageStart === u.pageEnd
                  ? `page ${u.pageStart}`
                  : `pages ${u.pageStart}–${u.pageEnd}`}{" "}
                couldn’t be read — enter those details manually.
              </li>
            ))}
            {report.unmapped.map((u) => (
              <li key={`${u.documentId}-${u.kind}`}>
                A {u.kind.replace(/_/g, " ")} form wasn’t used — enter its
                details manually.
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const FORM_LABELS: Record<string, string> = {
  acord_125: "ACORD 125",
  acord_126: "ACORD 126",
  acord_130: "ACORD 130",
  prior_policy: "a prior policy",
};

function formList(forms: string[]): string {
  const labels = forms.map((f) => FORM_LABELS[f] ?? f);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}
